// SCREENING JOBS (ACTIVE PROJECTS) ******************************************************
// pathname: /api/screenings/history
// ************************************************************************************************
import useSWR from 'swr';
import { fetcherScreening } from '@utils/data-fetching/fetcherScreening';
import type { ApiSchemaScreening } from '@type/api/apiUtilityFnScreening';

interface UseSwrScreeningJobsProps {
  shouldFetch?: boolean;
  page?: number;
  limit?: number;
}

/**
 * @description SWR: fetch screening jobs (active projects) list with full details and pagination metadata.
 * @param props - { UseSwrScreeningJobsProps } - Configuration object with optional shouldFetch and page.
 */
export const useSwrScreeningJobs = (props?: UseSwrScreeningJobsProps) => {
  const {
    shouldFetch = true, page = 1, limit,
  } = props ?? {};

  const urlParams = `?page=${page}${limit ? `&limit=${limit}` : ''}`;
  const swrKey = shouldFetch ? `/api/screenings/history${urlParams}` : null;

  const {
    data, error, isLoading, isValidating, mutate,
  } = useSWR<ApiSchemaScreening<'Screening.ScreeningHistoryOutput'>>(
    swrKey,
    async (url: string) => {
      const response = await fetcherScreening<ApiSchemaScreening<'Screening.ScreeningHistoryOutput'>>(url);
      if (response.error) {
        throw new Error('Failed to fetch screening jobs');
      }
      return response.data as ApiSchemaScreening<'Screening.ScreeningHistoryOutput'>;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // 1 minute
    },
  );

  // Return full response with pagination metadata
  // ********************************************
  return {
    data, // Full response including history array and pagination metadata
    error,
    mutate,
    isLoading,
    isValidating,
  };
};
